import { useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { geneticsApi } from '../../api/genetics';

interface AddMutationLocusFormProps {
  speciesId: string;
  initialDraft?: {
    locusSymbol: string;
    locusName: string;
    chromosome: string;
    inheritanceMode: string;
    description?: string;
    mutantSymbol: string;
    mutantName: string;
  };
  onCreated: (payload: {
    locusSymbol: string;
    locusName: string;
    description?: string;
    chromosome: string;
    inheritanceMode: string;
    alleles: Array<{ symbol: string; name: string; isWildType: boolean }>;
  }) => void | Promise<void>;
  onCancel: () => void;
}

const CHROMOSOME_OPTIONS = [
  { value: 'AUTOSOME', label: 'Autosomal' },
  { value: 'Z', label: 'Sex-linked (Z)' },
];

const INHERITANCE_OPTIONS = [
  { value: 'AUTOSOMAL_RECESSIVE', label: 'Autosomal recessive' },
  { value: 'AUTOSOMAL_DOMINANT', label: 'Autosomal dominant' },
  { value: 'AUTOSOMAL_INCOMPLETE_DOMINANT', label: 'Incomplete dominant' },
  { value: 'SEX_LINKED_RECESSIVE', label: 'Sex-linked recessive' },
  { value: 'CODOMINANT', label: 'Co-dominant' },
  { value: 'POLYGENIC', label: 'Polygenic (quantitative)' },
];

export default function AddMutationLocusForm({
  speciesId,
  initialDraft,
  onCreated,
  onCancel,
}: AddMutationLocusFormProps) {
  const [locusSymbol, setLocusSymbol] = useState(initialDraft?.locusSymbol ?? '');
  const [locusName, setLocusName] = useState(initialDraft?.locusName ?? '');
  const [chromosome, setChromosome] = useState(initialDraft?.chromosome ?? 'AUTOSOME');
  const [inheritanceMode, setInheritanceMode] = useState(
    initialDraft?.inheritanceMode ?? 'AUTOSOMAL_RECESSIVE',
  );
  const [description, setDescription] = useState(initialDraft?.description ?? '');
  const [mutantSymbol, setMutantSymbol] = useState(initialDraft?.mutantSymbol ?? '');
  const [mutantName, setMutantName] = useState(initialDraft?.mutantName ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!locusSymbol.trim() || !locusName.trim()) {
      setError('Locus symbol and name are required.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const trimmedSymbol = locusSymbol.trim();
      const wildSymbol = `${trimmedSymbol}+`;
      const alleles = [{ symbol: wildSymbol, name: 'Wild type', isWildType: true }];
      const mutantSym = mutantSymbol.trim();
      if (mutantSym && mutantName.trim() && mutantSym !== wildSymbol) {
        alleles.push({ symbol: mutantSym, name: mutantName.trim(), isWildType: false });
      }

      const payload = {
        locusSymbol: locusSymbol.trim(),
        locusName: locusName.trim(),
        description: description.trim() || undefined,
        chromosome,
        inheritanceMode,
        alleles,
      };
      await geneticsApi.createLocus(speciesId, payload);
      await onCreated(payload);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create locus');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-md border border-indigo-200 bg-indigo-50/40 p-3 space-y-3">
      <h5 className="text-sm font-medium text-indigo-900 flex items-center gap-1">
        <PlusIcon className="h-4 w-4" />
        Add mutation locus
      </h5>

      {error && <p className="text-xs text-red-600">{error}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Symbol</label>
          <input
            type="text"
            value={locusSymbol}
            onChange={(e) => setLocusSymbol(e.target.value)}
            placeholder="e.g. bl"
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Name</label>
          <input
            type="text"
            value={locusName}
            onChange={(e) => setLocusName(e.target.value)}
            placeholder="e.g. Blue"
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Chromosome</label>
          <select
            value={chromosome}
            onChange={(e) => setChromosome(e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          >
            {CHROMOSOME_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Inheritance</label>
          <select
            value={inheritanceMode}
            onChange={(e) => setInheritanceMode(e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          >
            {INHERITANCE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Mutant allele symbol (optional)</label>
          <input
            type="text"
            value={mutantSymbol}
            onChange={(e) => setMutantSymbol(e.target.value)}
            placeholder="e.g. bl"
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Mutant allele name (optional)</label>
          <input
            type="text"
            value={mutantName}
            onChange={(e) => setMutantName(e.target.value)}
            placeholder="e.g. Blue"
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-slate-700 mb-1">Visual effect</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Removes yellow psittacin so green becomes blue; autosomal recessive — visual only when homozygous."
            rows={3}
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          />
          <p className="mt-1 text-xs text-slate-500">
            Short visual effect plus how the gene works (pigment change, dominance, sex-linked, etc.).
          </p>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={saving}
          className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Create locus'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
